import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { updateAdStep2, getMyPaymentMethods, getMyAds } from '../../../services/p2pService';
import { useToast } from '../../../context/ToastContext';
import { getCachedBalance } from '../../../services/walletService';
import { authFetch, getAuthHeaders } from '../../../lib/authFetch';

const TIME_LIMITS = [15, 30, 60];
const KEY = 'p2p_step2';
function loadS() { try { return JSON.parse(sessionStorage.getItem(KEY) || '{}'); } catch { return {}; } }
function saveS(d: object) { sessionStorage.setItem(KEY, JSON.stringify(d)); }

function InputWithPostfix({ label, value, onChange, placeholder, postfix }: { label: string; value: string; onChange: (v: string) => void; placeholder: string; postfix: string }) {
    return (
        <div>
            <div style={{ fontSize: 13, color: 'var(--p2p-text-muted)', marginBottom: 8 }}>{label}</div>
            <div style={{ position: 'relative' }}>
                <input
                    className="p2p-input"
                    style={{ paddingRight: postfix.length * 9 + 16 }}
                    placeholder={placeholder}
                    value={value}
                    onChange={e => onChange(e.target.value.replace(/[^0-9.]/g, ''))}
                />
                <span style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 13, fontWeight: 700, color: 'var(--p2p-text-muted)', pointerEvents: 'none' }}>
                    {postfix}
                </span>
            </div>
        </div>
    );
}

export default function PostAdStep2() {
    const navigate = useNavigate();
    const { toast } = useToast();
    const saved = loadS();
    const [totalAmount, setTotalAmount] = useState(saved.totalAmount || '');
    const [minOrder, setMinOrder] = useState(saved.minOrder || '');
    const [maxOrder, setMaxOrder] = useState(saved.maxOrder || '');
    const [timeLimit, setTimeLimit] = useState(saved.timeLimit || 15);
    const [myMethods, setMyMethods] = useState<any[]>([]);
    const [selectedPms, setSelectedPms] = useState<string[]>(saved.selectedPms || []);
    const [loading, setLoading] = useState(false);
    // Read step1 data immediately from sessionStorage so postfix is correct on mount
    const step1 = (() => { try { return JSON.parse(sessionStorage.getItem('p2p_step1') || '{}'); } catch { return {}; } })();
    const [crypto, setCrypto] = useState(step1.crypto || 'USDT');
    const [fiat, setFiat] = useState(step1.fiat || 'KES');
    const [side, setSide] = useState(step1.side || 'sell');
    const [availableBalance, setAvailableBalance] = useState(0);
    const [adPostFee, setAdPostFee] = useState(0.02); // fetched from backend
    const [adPostFeeType, setAdPostFeeType] = useState<'percentage' | 'fixed'>('percentage');
    const [adPostFeeEnabled, setAdPostFeeEnabled] = useState(true);

    // Fixed = flat amount in the ad's own asset, regardless of totalAmount.
    // Percentage = adPostFee is a fraction (e.g. 0.02 = 2%) of totalAmount.
    const feeFor = (amount: number) => !adPostFeeEnabled ? 0 : adPostFeeType === 'fixed' ? adPostFee : amount * adPostFee;
    const maxSellable = () => adPostFeeType === 'fixed' ? Math.max(0, availableBalance - adPostFee) : availableBalance / (1 + adPostFee);

    // Auto-set maxOrder = totalAmount × price whenever totalAmount changes
    const handleTotalAmountChange = (val: string) => {
        const cleaned = val.replace(/[^0-9.]/g, '');
        setTotalAmount(cleaned);
        const s1 = (() => { try { return JSON.parse(sessionStorage.getItem('p2p_step1') || '{}'); } catch { return {}; } })();
        const price = parseFloat(s1.fixedPrice || '0');
        if (price > 0 && parseFloat(cleaned) > 0) {
            const fiatMax = parseFloat((parseFloat(cleaned) * price).toFixed(2));
            setMaxOrder(fiatMax.toString());
        }
    };

    // Max fiat equivalent of total amount — used to cap maxOrder
    const getMaxFiatEquivalent = (): number => {
        const s1 = (() => { try { return JSON.parse(sessionStorage.getItem('p2p_step1') || '{}'); } catch { return {}; } })();
        const price = parseFloat(s1.fixedPrice || '0');
        const total = parseFloat(totalAmount || '0');
        if (price > 0 && total > 0) return parseFloat((total * price).toFixed(2));
        return 0;
    };

    const handleMaxOrderChange = (val: string) => {
        const cleaned = val.replace(/[^0-9.]/g, '');
        const maxFiat = getMaxFiatEquivalent();
        if (maxFiat > 0 && parseFloat(cleaned) > maxFiat) {
            setMaxOrder(maxFiat.toString());
        } else {
            setMaxOrder(cleaned);
        }
    };

    // Persist on every change
    useEffect(() => {
        saveS({ totalAmount, minOrder, maxOrder, timeLimit, selectedPms });
    }, [totalAmount, minOrder, maxOrder, timeLimit, selectedPms]);

    useEffect(() => {
        getMyPaymentMethods().then(setMyMethods).catch(() => {});
        // Sync from draft in case Step1 session was cleared — only updates if draft differs
        const draftId = sessionStorage.getItem('p2p_draft_id');
        if (draftId) {
            getMyAds().then(ads => {
                const draft = ads.find((a: any) => a._id === draftId);
                if (draft) {
                    setCrypto(draft.crypto);
                    setFiat(draft.fiat);
                    setSide(draft.side);
                }
            }).catch(() => {});
        }
        // Fetch live balance
        authFetch(`${import.meta.env.VITE_API_BASE_URL}/api/users/profile`, { headers: getAuthHeaders() })
            .then(r => r.json())
            .then(d => setAvailableBalance(d?.balance ?? 0))
            .catch(() => setAvailableBalance(getCachedBalance()));
        // Fetch real ad post fee from backend
        authFetch(`${import.meta.env.VITE_API_BASE_URL}/api/p2p/settings/fees`, { headers: getAuthHeaders() })
            .then(r => r.json())
            .then(d => {
                if (d?.data?.fees) {
                    setAdPostFee(d.data.fees.adPostFee ?? 0.02);
                    setAdPostFeeType(d.data.fees.adPostFeeType ?? 'percentage');
                    setAdPostFeeEnabled(d.data.fees.adPostFeeEnabled ?? true);
                }
            })
            .catch(() => {});
    }, []);

    const togglePm = (id: string) => setSelectedPms(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev.slice(0, 4), id]);

    const next = async () => {
        const draftId = sessionStorage.getItem('p2p_draft_id');
        if (!draftId) { toast('Draft not found. Start over.', 'error'); navigate('/p2p/post-ad/1'); return; }
        if (!totalAmount || !minOrder || !maxOrder) { toast('Fill all amount fields', 'error'); return; }
        const total = parseFloat(totalAmount);
        const min = parseFloat(minOrder);
        const max = parseFloat(maxOrder);
        if (isNaN(total) || total <= 0) { toast('Enter a valid total amount', 'error'); return; }
        if (min > max) { toast('Min order cannot exceed max order', 'error'); return; }
        // Max order limit (in fiat) must correspond to no more than the totalAmount in crypto
        // We can't always compare fiat vs crypto directly, but we can enforce max crypto-equivalent
        // by checking if maxOrder fiat value exceeds what's available
        if (side === 'sell' && availableBalance > 0) {
            const needed = total + feeFor(total);
            if (needed > availableBalance) { toast(`Insufficient balance. Need ${needed.toFixed(6)} ${crypto}`, 'error'); return; }
        }
        if (selectedPms.length === 0) { toast('Select at least one payment method', 'error'); return; }
        setLoading(true);
        try {
            await updateAdStep2(draftId, { totalAmount: total, minOrderAmount: min, maxOrderAmount: max, paymentMethods: selectedPms, paymentTimeLimit: timeLimit });
            navigate('/p2p/post-ad/3');
        } catch (e: any) { toast(e.message, 'error'); }
        setLoading(false);
    };

    return (
        <div style={{ background: 'var(--p2p-bg-primary)', height: '100dvh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div className="p2p-header" style={{ flexShrink: 0 }}>
                <button className="p2p-back-btn" onClick={() => navigate(-1)}><ArrowLeft size={20} />Post Ad</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 20 }}>
                <div className="p2p-steps">
                    <div className="p2p-step-dot done">✓</div>
                    <div className="p2p-step-line done" />
                    <div className="p2p-step-dot active">2</div>
                    <div className="p2p-step-line" />
                    <div className="p2p-step-dot">3</div>
                </div>

                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <div style={{ fontSize: 13, color: 'var(--p2p-text-muted)' }}>Total Amount</div>
                        {side === 'sell' && availableBalance > 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: 12, color: 'var(--p2p-text-muted)' }}>
                                    Balance: <strong style={{ color: 'var(--p2p-text-primary)' }}>{parseFloat(availableBalance.toFixed(8)).toString()} {crypto}</strong>
                                </span>
                                <button
                                    onClick={() => handleTotalAmountChange(Math.floor(maxSellable()).toString())}
                                    style={{ fontSize: 11, padding: '3px 10px', borderRadius: 6, border: '1px solid var(--p2p-buy)', background: 'var(--p2p-buy-subtle)', color: 'var(--p2p-buy)', cursor: 'pointer', fontWeight: 700 }}
                                >
                                    Max
                                </button>
                            </div>
                        )}
                    </div>
                    <div style={{ position: 'relative' }}>
                        <input
                            className="p2p-input"
                            style={{ paddingRight: crypto.length * 9 + 16, borderColor: side === 'sell' && totalAmount && parseFloat(totalAmount) + feeFor(parseFloat(totalAmount)) > availableBalance ? 'var(--p2p-sell)' : undefined }}
                            placeholder="0.00"
                            value={totalAmount}
                            onChange={e => handleTotalAmountChange(e.target.value)}
                        />
                        <span style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 13, fontWeight: 700, color: 'var(--p2p-text-muted)', pointerEvents: 'none' }}>{crypto}</span>
                    </div>
                    {/* Balance + fee validation for sell */}
                    {side === 'sell' && totalAmount && parseFloat(totalAmount) > 0 && (
                        <div style={{ marginTop: 8 }}>
                            {(() => {
                                const amount = parseFloat(totalAmount);
                                const fee = feeFor(amount);
                                const total = amount + fee;
                                const exceeds = total > availableBalance;
                                const maxAllowed = maxSellable();
                                const fmt = (n: number) => parseFloat(n.toFixed(8)).toString();
                                const feeLabel = adPostFeeType === 'fixed' ? `flat ${fmt(adPostFee)} ${crypto}` : `${(adPostFee * 100).toFixed(0)}%`;
                                return (
                                    <div style={{ fontSize: 12, color: exceeds ? 'var(--p2p-sell)' : 'var(--p2p-text-muted)' }}>
                                        {exceeds
                                            ? `Insufficient balance. Need ${fmt(total)} ${crypto} (amount + ${feeLabel} fee). Max you can sell: ${fmt(maxAllowed)} ${crypto}`
                                            : `Fee: ${fmt(fee)} ${crypto} · Total deducted: ${fmt(total)} ${crypto}`
                                        }
                                    </div>
                                );
                            })()}
                        </div>
                    )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <InputWithPostfix label="Min Order" value={minOrder} onChange={setMinOrder} placeholder="Min" postfix={fiat} />
                    <div>
                        <div style={{ fontSize: 13, color: 'var(--p2p-text-muted)', marginBottom: 8 }}>Max Order</div>
                        <div style={{ position: 'relative' }}>
                            <input
                                className="p2p-input"
                                style={{
                                    paddingRight: fiat.length * 9 + 16,
                                    borderColor: (() => {
                                        const maxFiat = getMaxFiatEquivalent();
                                        return maxFiat > 0 && parseFloat(maxOrder) > maxFiat ? 'var(--p2p-sell)' : undefined;
                                    })(),
                                }}
                                placeholder="Max"
                                value={maxOrder}
                                onChange={e => handleMaxOrderChange(e.target.value)}
                            />
                            <span style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 13, fontWeight: 700, color: 'var(--p2p-text-muted)', pointerEvents: 'none' }}>{fiat}</span>
                        </div>
                        {(() => {
                            const maxFiat = getMaxFiatEquivalent();
                            if (maxFiat > 0 && maxOrder && parseFloat(maxOrder) >= maxFiat) {
                                return <div style={{ fontSize: 11, color: 'var(--p2p-text-muted)', marginTop: 4 }}>Max: {maxFiat.toLocaleString()} {fiat}</div>;
                            }
                            return null;
                        })()}
                    </div>
                </div>

                <div>
                    <div style={{ fontSize: 13, color: 'var(--p2p-text-muted)', marginBottom: 8 }}>Payment Time Limit</div>
                    <div className="p2p-chip-row">{TIME_LIMITS.map(t => <button key={t} className={`p2p-chip ${timeLimit === t ? 'selected' : ''}`} onClick={() => setTimeLimit(t)}>{t} min</button>)}</div>
                </div>

                <div>
                    <div style={{ fontSize: 13, color: 'var(--p2p-text-muted)', marginBottom: 8 }}>Payment Methods (up to 5)</div>
                    {myMethods.length === 0 ? (
                        <div style={{ padding: 16, border: '1px dashed var(--p2p-border-input)', borderRadius: 8, textAlign: 'center' }}>
                            <span style={{ fontSize: 13, color: 'var(--p2p-text-muted)' }}>No payment methods. </span>
                            <button style={{ background: 'none', border: 'none', color: 'var(--p2p-buy)', cursor: 'pointer', fontSize: 13 }} onClick={() => navigate('/p2p/payment-methods/add')}>Add one</button>
                        </div>
                    ) : myMethods.map(m => (
                        <label key={m._id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid var(--p2p-border-default)', cursor: 'pointer' }}>
                            <input type="checkbox" style={{ accentColor: 'var(--p2p-buy)', width: 16, height: 16 }} checked={selectedPms.includes(m._id)} onChange={() => togglePm(m._id)} />
                            <span className="p2p-pm-dot" style={{ background: m.methodColor }} />
                            <span style={{ fontSize: 14, color: 'var(--p2p-text-primary)' }}>{m.methodType}</span>
                            <span style={{ fontSize: 12, color: 'var(--p2p-text-muted)', marginLeft: 'auto', textAlign: 'right' }}>
                                {m.accountName}
                                {m.extraFields?.bankName && <><br /><span style={{ fontSize: 11, color: 'var(--p2p-text-disabled)' }}>{m.extraFields.bankName}</span></>}
                            </span>
                        </label>
                    ))}
                </div>

                {/* Fee estimate */}
                {adPostFeeEnabled && totalAmount && parseFloat(totalAmount) > 0 && (
                    <div style={{ background: 'var(--p2p-buy-subtle)', border: '1px solid var(--p2p-buy-border)', borderRadius: 8, padding: 12, fontSize: 13, color: 'var(--p2p-buy)', display: 'flex', justifyContent: 'space-between' }}>
                        <span>Estimated publish fee {adPostFeeType === 'percentage' ? `(${(adPostFee * 100).toFixed(2)}%)` : '(flat)'}</span>
                        <strong>{parseFloat(feeFor(parseFloat(totalAmount)).toFixed(8)).toString()} {crypto}</strong>
                    </div>
                )}

            </div>
            <div style={{ flexShrink:0, padding:'12px 16px calc(12px + env(safe-area-inset-bottom)) 16px', marginBottom:'calc(64px + env(safe-area-inset-bottom))', borderTop:'1px solid var(--p2p-border-default)', background:'var(--p2p-bg-primary)' }}>
                <button
                    className="p2p-btn-yellow"
                    disabled={
                        loading ||
                        !totalAmount || parseFloat(totalAmount) <= 0 ||
                        !minOrder || !maxOrder ||
                        parseFloat(minOrder) > parseFloat(maxOrder) ||
                        (getMaxFiatEquivalent() > 0 && parseFloat(maxOrder) > getMaxFiatEquivalent()) ||
                        selectedPms.length === 0 ||
                        (side === 'sell' && parseFloat(totalAmount) * 1.02 > availableBalance)
                    }
                    onClick={next}
                >
                    {loading ? 'Saving...' : 'Next'}
                </button>
            </div>
        </div>
    );
}
